import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { supabase } from '../../lib/supabase';
import {
  registerForEventWithRef,
  captureEarlyLead,
  completeRegistrationFromLead,
} from '../../services/registrationsService';
import StripeCheckout from '../StripeCheckout';
import {
  REGISTRATION_COPY,
  normalizeEventLanguage,
} from './registrationConstants';
import styles from './RegistrationFlow.module.css';

const { FiUser, FiMail, FiPhone, FiLock, FiCheck } = FiIcons;

function LanguageToggle({ lang, onChange, variant }) {
  const activeClass =
    variant === 'embedded'
      ? styles.langToggleBtnActiveEmbedded
      : styles.langToggleBtnActiveStandalone;

  return (
    <div className={styles.langToggle} role="group" aria-label="Language">
      <button
        type="button"
        onClick={() => onChange('en')}
        className={`${styles.langToggleBtn} ${lang === 'en' ? activeClass : ''}`}
      >
        EN
      </button>
      <button
        type="button"
        onClick={() => onChange('es')}
        className={`${styles.langToggleBtn} ${lang === 'es' ? activeClass : ''}`}
      >
        ES
      </button>
    </div>
  );
}

/**
 * Shared multi-step registration flow (personal info → payment/confirm → account).
 * Used standalone at /register/:eventId and embedded in landing page hero.
 */
export default function RegistrationFlow({
  variant = 'standalone',
  eventId,
  event,
  refCode = null,
  queryLeadId = null,
  queryEmail = null,
}) {
  const navigate = useNavigate();
  const isEmbedded = variant === 'embedded';

  const [lang, setLang] = useState('en');
  const userChangedLangRef = useRef(false);
  const { register, handleSubmit, formState: { errors }, watch, trigger, setValue } = useForm();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [stepOneLoading, setStepOneLoading] = useState(false);
  const [leadId, setLeadId] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('idle');
  const [, setSharingLinkId] = useState(null);
  const [, setSharingLinkAgentId] = useState(null);
  const [resumeFromLeadLoading, setResumeFromLeadLoading] = useState(false);
  const [showOrientationChoice, setShowOrientationChoice] = useState(false);
  const [showOrientationCompleteLater, setShowOrientationCompleteLater] = useState(false);
  const [stepTwoFreeLoading, setStepTwoFreeLoading] = useState(false);
  const [registrationCompleteWithoutAccount, setRegistrationCompleteWithoutAccount] = useState(false);

  const t = REGISTRATION_COPY[lang] ?? REGISTRATION_COPY.en;

  useEffect(() => {
    if (event?.id) {
      setLang(normalizeEventLanguage(event.language));
    }
  }, [event?.id]);

  useEffect(() => {
    if (!userChangedLangRef.current) return;
    userChangedLangRef.current = false;
    trigger();
  }, [lang, trigger]);

  const handleLangChange = (next) => {
    if (next === lang) return;
    userChangedLangRef.current = true;
    setLang(next);
  };

  useEffect(() => {
    let isActive = true;

    const fetchSharingLink = async () => {
      if (!refCode) {
        if (isActive) {
          setSharingLinkId(null);
          setSharingLinkAgentId(null);
        }
        return;
      }

      const normRef = String(refCode).trim().toLowerCase();
      let query = supabase
        .from('agent_sharing_links')
        .select('id, agent_id, slug')
        .eq('slug', normRef);
      if (eventId) query = query.eq('event_id', eventId);
      const { data, error } = await query.maybeSingle();

      if (!isActive) return;

      if (error) {
        console.warn('[RegistrationFlow] Sharing link lookup failed:', error);
        setSharingLinkId(null);
        setSharingLinkAgentId(null);
        return;
      }

      setSharingLinkId(data?.id ?? null);
      setSharingLinkAgentId(data?.agent_id ?? null);
    };

    fetchSharingLink();
    return () => { isActive = false; };
  }, [refCode, eventId]);

  useEffect(() => {
    if (!eventId || !queryLeadId || !queryEmail?.trim() || !event) {
      return;
    }
    let isActive = true;
    setResumeFromLeadLoading(true);
    completeRegistrationFromLead(queryLeadId, eventId, queryEmail)
      .then(({ data, error }) => {
        if (!isActive) return;
        setResumeFromLeadLoading(false);
        if (error || !data) {
          if (error?.message) toast.error(error.message);
          return;
        }
        setLeadId(data.lead.id);
        setValue('name', data.lead.full_name ?? '');
        setValue('email', data.lead.email ?? '');
        setValue('phone', data.lead.phone ?? '');
        setStep(2);
      })
      .catch(() => {
        if (isActive) setResumeFromLeadLoading(false);
      });
    return () => { isActive = false; };
  }, [eventId, queryLeadId, queryEmail, event?.id, setValue]);

  if (resumeFromLeadLoading) {
    return (
      <div className={isEmbedded ? styles.embeddedRoot : 'py-8 text-center'}>
        <div className={`w-8 h-8 border-4 border-gray-200 rounded-full animate-spin mx-auto mb-4 ${isEmbedded ? '' : 'border-t-blue-600'}`}
          style={isEmbedded ? { borderTopColor: 'var(--lp-accent)' } : undefined}
        />
        <p className={isEmbedded ? 'text-sm' : 'text-gray-600'} style={isEmbedded ? { color: 'color-mix(in srgb, var(--lp-primary) 65%, white)' } : undefined}>
          {t.loadingRegistration}
        </p>
      </div>
    );
  }

  const normalizedRef = (refCode && String(refCode).trim().toLowerCase()) || null;

  const onSubmit = async (data) => {
    setLoading(true);

    try {
      if (event.price > 0 && !paymentIntentId) {
        throw new Error(t.errPaymentRequired);
      }

      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            full_name: data.name?.trim() ?? '',
            phone: data.phone?.trim() || null,
            role: 'student',
          },
          emailRedirectTo: typeof window !== 'undefined' ? window.location.origin + '/auth/callback' : undefined,
        },
      });

      if (signUpError) throw signUpError;
      if (!signUpData?.user?.id) throw new Error(t.errUnableCreateAccount);

      const { error: registrationError } = await registerForEventWithRef(
        eventId,
        normalizedRef,
        {
          full_name: data.name,
          email: data.email,
          phone: data.phone || null,
          invited_by: data.invitedBy || null,
        },
        {
          studentId: signUpData.user.id,
          leadId: leadId || null,
          paymentIntentId: paymentIntentId || null,
          paymentAmount: event?.price ?? null,
          status: 'registered',
          paymentStatus: 'completed',
        }
      );

      if (registrationError) {
        throw new Error(registrationError.message || t.errRegistrationComplete);
      }

      toast.success(t.toastRegistrationSuccess);
      navigate('/dashboard/student-dashboard');
    } catch (error) {
      console.error('[RegistrationFlow] Failed:', error);
      toast.error(error.message || t.toastRegistrationFailed);
    } finally {
      setLoading(false);
    }
  };

  const logStepAdvance = (currentStep) => {
    console.log('Moving from step', currentStep, 'to', currentStep + 1);
    console.log('Event price:', event?.price ?? 0);
    console.log('Should show payment?', (event?.price ?? 0) > 0);
  };

  const nextStep = () => {
    setStep((currentStep) => {
      logStepAdvance(currentStep);
      return currentStep + 1;
    });
  };
  const prevStep = () => setStep(step - 1);

  const handleStepOneContinue = async () => {
    const valid = await trigger(['name', 'email']);
    if (!valid) return;

    const name = watch('name');
    const email = watch('email');
    const phone = watch('phone');
    const invitedBy = watch('invitedBy');
    setStepOneLoading(true);
    setShowOrientationCompleteLater(false);
    try {
      const { data: captureData, error: captureError } = await captureEarlyLead(
        eventId,
        {
          name: name?.trim() ?? '',
          email: email?.trim(),
          phone: phone?.trim() || null,
          invited_by: invitedBy?.trim() || null,
        },
        normalizedRef
      );
      if (captureError) {
        toast.error(captureError.message || t.toastCaptureFailed);
        return;
      }
      if (captureData?.ok && captureData?.lead_id) {
        setLeadId(captureData.lead_id);
      }
      if (captureData?.has_free_orientation) {
        setShowOrientationChoice(true);
        setStep((currentStep) => {
          logStepAdvance(currentStep);
          return 2;
        });
        setStepOneLoading(false);
        return;
      }
      setStep((currentStep) => {
        logStepAdvance(currentStep);
        return 2;
      });
    } catch (err) {
      console.error('[RegistrationFlow] Step 1 capture failed:', err);
      toast.error(err.message || t.toastGenericError);
    } finally {
      setStepOneLoading(false);
    }
  };

  const handleContinueToPremium = () => {
    setShowOrientationChoice(false);
  };

  const handleCompleteLater = () => {
    setShowOrientationCompleteLater(true);
  };

  const handlePaymentStatus = async (status, paymentIntent) => {
    setPaymentStatus(status);
    if (paymentIntent?.id) {
      setPaymentIntentId(paymentIntent.id);
    }
    if (status === 'succeeded') {
      const payload = {
        full_name: watch('name'),
        email: watch('email'),
        phone: watch('phone') || null,
        invited_by: watch('invitedBy') || null,
      };
      await registerForEventWithRef(
        eventId,
        normalizedRef,
        payload,
        {
          leadId: leadId || null,
          paymentIntentId: paymentIntent?.id || paymentIntentId || null,
          paymentAmount: event?.price ?? null,
          status: 'registered',
          paymentStatus: 'completed',
        }
      );
      setStep(3);
    }
  };

  const pct = Math.round((step / 3) * 100);

  const inputClass = isEmbedded
    ? `${styles.embeddedInput} ${styles.embeddedInputWithIcon}`
    : 'pl-10 w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent';

  const inputClassNoIcon = isEmbedded
    ? styles.embeddedInput
    : 'w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent';

  const btnPrimaryClass = isEmbedded
    ? styles.embeddedBtnPrimary
    : 'w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50';

  const btnPrimaryFlexClass = isEmbedded
    ? `${styles.embeddedBtnPrimary} ${styles.embeddedBtnPrimaryFlex}`
    : 'flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:cursor-not-allowed disabled:opacity-50';

  const btnSecondaryClass = isEmbedded
    ? styles.embeddedBtnSecondary
    : 'flex-1 border border-gray-300 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-50 transition-colors';

  const progressBar = isEmbedded ? (
    <div className={styles.embeddedProgress}>
      <div className={styles.embeddedProgressLabels}>
        <span>{t.stepOf(step)}</span>
        <span>{t.pctComplete(pct)}</span>
      </div>
      <div className={styles.embeddedProgressTrack}>
        <div className={styles.embeddedProgressFill} style={{ width: `${(step / 3) * 100}%` }} />
      </div>
    </div>
  ) : (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-600">{t.stepOf(step)}</span>
        <span className="text-sm text-gray-600">{t.pctComplete(pct)}</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-in-out"
          style={{ width: `${(step / 3) * 100}%` }}
        />
      </div>
    </div>
  );

  const stepPanelClass = isEmbedded
    ? `${styles.embeddedPanel} relative`
    : 'relative bg-white rounded-lg shadow-lg p-8';

  const stepContent = (
    <>
      {progressBar}

      <motion.div
        key={step}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className={stepPanelClass}
      >
        <div className={isEmbedded ? styles.embeddedLangToggle : 'absolute top-4 right-4 z-10'}>
          <LanguageToggle lang={lang} onChange={handleLangChange} variant={variant} />
        </div>

        {step === 1 && (
          <div className={`space-y-6 pt-2 sm:pt-0 ${isEmbedded ? 'sm:pr-20' : 'sm:pr-28'}`}>
            <h2 className={`text-xl font-semibold text-gray-900 mb-4 pr-16 sm:pr-0 ${isEmbedded ? '!text-[color-mix(in_srgb,var(--lp-primary)_90%,white)]' : ''}`}>
              {t.personalInformation}
            </h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t.labelFullName}</label>
              <div className="relative">
                <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input type="text" {...register('name', { required: t.errFullName })} className={inputClass} placeholder={t.phFullName} />
              </div>
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t.labelEmail}</label>
              <div className="relative">
                <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  {...register('email', {
                    required: t.errEmailRequired,
                    pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: t.errEmailInvalid },
                  })}
                  className={inputClass}
                  placeholder={t.phEmail}
                />
              </div>
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t.labelPhone}</label>
              <div className="relative">
                <SafeIcon icon={FiPhone} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input type="tel" {...register('phone')} className={inputClass} placeholder={t.phPhone} />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t.labelInvitedBy}</label>
              <input
                type="text"
                maxLength={100}
                {...register('invitedBy', { maxLength: { value: 100, message: t.errInvitedByMax } })}
                className={inputClassNoIcon}
                placeholder={t.phOptional}
              />
              {errors.invitedBy && <p className="text-red-500 text-sm mt-1">{errors.invitedBy.message}</p>}
            </div>

            <button type="button" onClick={handleStepOneContinue} disabled={stepOneLoading} className={btnPrimaryClass}>
              {stepOneLoading ? t.btnProcessing : t.btnContinue}
            </button>
          </div>
        )}

        {step === 2 && (
          <div className={`space-y-6 pt-2 sm:pt-0 ${isEmbedded ? 'sm:pr-20' : 'sm:pr-28'}`}>
            {showOrientationCompleteLater ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                <h2 className="text-xl font-semibold text-green-900 mb-2">{t.orientationLaterTitle}</h2>
                <p className="text-gray-700 mb-4">{t.orientationLaterBody}</p>
                <p className="text-sm text-gray-600">
                  {t.orientationLaterInboxPrefix} <strong>{watch('email')}</strong> {t.orientationLaterInboxSuffix}
                </p>
              </div>
            ) : showOrientationChoice ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                <h2 className="text-xl font-semibold text-green-900 mb-2">{t.orientationRegisteredTitle}</h2>
                <p className="text-gray-700 mb-6">{t.orientationRegisteredBody}</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button type="button" onClick={handleContinueToPremium} className={isEmbedded ? `${styles.embeddedBtnPrimary} sm:flex-initial sm:w-auto` : 'flex-1 sm:flex-initial bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors'}>
                    {t.btnContinuePremium}
                  </button>
                  <button type="button" onClick={handleCompleteLater} className={isEmbedded ? `${styles.embeddedBtnSecondary} sm:flex-initial` : 'flex-1 sm:flex-initial border border-gray-300 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-50 transition-colors'}>
                    {t.btnCompleteLater}
                  </button>
                </div>
              </div>
            ) : event.price > 0 ? (
              <>
                <h2 className="text-xl font-semibold text-gray-900 mb-4 pr-16 sm:pr-0">{t.paymentInformation}</h2>

                <div className={isEmbedded ? styles.embeddedSummaryBox : 'bg-blue-50 rounded-lg p-4 mb-6'}>
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-900">{event.title}</span>
                    <span className={`text-2xl font-bold ${isEmbedded ? styles.embeddedPriceAccent : 'text-blue-600'}`}>${event.price}</span>
                  </div>
                </div>

                <StripeCheckout
                  amount={event.price}
                  description={event.title}
                  customerEmail={watch('email')}
                  metadata={{ eventId, leadId }}
                  eventId={eventId}
                  usePlatformAccount={event.use_platform_account}
                  returnUrl={typeof window === 'undefined' ? '' : window.location.href}
                  onPaymentStatus={handlePaymentStatus}
                />

                {paymentStatus === 'succeeded' ? (
                  <p className="text-sm text-green-600">{t.paymentConfirmed}</p>
                ) : (
                  <p className="text-sm text-gray-600">{t.paymentContinuePrompt}</p>
                )}

                <div className="flex space-x-4">
                  <button type="button" onClick={prevStep} className={btnSecondaryClass}>{t.btnBack}</button>
                  <button type="button" onClick={nextStep} disabled={paymentStatus !== 'succeeded'} className={btnPrimaryFlexClass}>
                    {t.btnContinue}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-xl font-semibold text-gray-900 mb-4 pr-16 sm:pr-0">{t.confirmRegistration}</h2>

                <div className={isEmbedded ? styles.embeddedSummaryBox : 'bg-blue-50 rounded-lg p-4 mb-6'}>
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-900">{event.title}</span>
                    <span className="text-2xl font-bold text-green-600">{t.priceFree}</span>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                  <h3 className="font-semibold text-gray-900">{t.eventSummary}</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t.summaryEvent}</span>
                      <span className="font-medium">{event.title}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t.summaryDate}</span>
                      <span>{event.date}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t.summaryTime}</span>
                      <span>{event.time}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t.summaryPrice}</span>
                      <span className="font-bold text-green-600">{t.priceFree}</span>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-4">
                  <button type="button" onClick={prevStep} className={btnSecondaryClass}>{t.btnBack}</button>
                  <button
                    type="button"
                    disabled={stepTwoFreeLoading}
                    onClick={async () => {
                      setStepTwoFreeLoading(true);
                      try {
                        const { error: regError } = await registerForEventWithRef(
                          eventId,
                          normalizedRef,
                          {
                            full_name: watch('name'),
                            email: watch('email'),
                            phone: watch('phone') || null,
                            invited_by: watch('invitedBy') || null,
                          },
                          { leadId: leadId || null, status: 'registered', paymentStatus: 'completed' }
                        );
                        if (regError) {
                          toast.error(regError.message || t.toastConfirmFailed);
                          return;
                        }
                        nextStep();
                      } catch (e) {
                        toast.error(e.message || t.toastGenericError);
                      } finally {
                        setStepTwoFreeLoading(false);
                      }
                    }}
                    className={btnPrimaryFlexClass}
                  >
                    {stepTwoFreeLoading ? t.btnConfirming : t.btnContinue}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {step === 3 && registrationCompleteWithoutAccount ? (
          <div className={`space-y-6 text-center pt-2 sm:pt-0 ${isEmbedded ? 'sm:pr-20' : 'sm:pr-28'}`}>
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-green-900 mb-2">{t.registeredSuccessTitle}</h2>
              <p className="text-gray-700 mb-4">
                {t.registeredSuccessBodyPrefix} <strong>{event.title}</strong>. {t.registeredSuccessBodySuffix}
              </p>
              <p className="text-sm text-gray-600">{t.registeredSuccessFooter}</p>
            </div>
            {isEmbedded ? (
              <Link to="/" className={styles.embeddedReturnLink}>{t.linkReturnHome}</Link>
            ) : (
              <Link to="/" className="inline-block bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors">
                {t.linkReturnHome}
              </Link>
            )}
          </div>
        ) : step === 3 && (
          <form onSubmit={handleSubmit(onSubmit)} className={`space-y-6 pt-2 sm:pt-0 ${isEmbedded ? 'sm:pr-20' : 'sm:pr-28'}`}>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-1 pr-14 sm:pr-0">{t.accountCreateTitle}</h2>
              <p className="text-sm text-gray-600">{t.accountCreateSubtitle}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t.labelPassword}</label>
              <div className="relative">
                <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="password"
                  {...register('password', { required: t.errPasswordRequired, minLength: { value: 6, message: t.errPasswordMin } })}
                  className={inputClass}
                  placeholder={t.phPassword}
                />
              </div>
              {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t.labelConfirmPassword}</label>
              <div className="relative">
                <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="password"
                  {...register('confirmPassword', {
                    required: t.errConfirmPassword,
                    validate: (value) => value === watch('password') || t.errPasswordMismatch,
                  })}
                  className={inputClass}
                  placeholder={t.phConfirmPassword}
                />
              </div>
              {errors.confirmPassword && <p className="text-red-500 text-sm mt-1">{errors.confirmPassword.message}</p>}
            </div>

            <h3 className="text-lg font-semibold text-gray-900">{t.eventSummaryShort}</h3>

            <div className="bg-gray-50 rounded-lg p-6 space-y-4">
              <p className="text-sm text-gray-600">{t.accountNoteRegistered}</p>
              <h3 className="font-semibold text-gray-900">{t.eventSummary}</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t.summaryEvent}</span>
                  <span className="font-medium">{event.title}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t.summaryDate}</span>
                  <span>{event.date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t.summaryTime}</span>
                  <span>{event.time}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t.summaryPrice}</span>
                  <span className="font-bold text-green-600">
                    {event.price === 0 ? t.priceFree : `$${event.price}`}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600" />
                <span className="text-green-800 font-medium">{t.whatHappensNext}</span>
              </div>
              <ul className="mt-3 space-y-2 text-sm text-green-700">
                <li>• {t.nextBullet1}</li>
                <li>• {t.nextBullet2}</li>
                <li>• {t.nextBullet3}</li>
                <li>• {t.nextBullet4}</li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button type="button" onClick={prevStep} className={btnSecondaryClass}>{t.btnBack}</button>
              <button
                type="button"
                onClick={() => {
                  toast.success(t.toastSkipSuccess);
                  setRegistrationCompleteWithoutAccount(true);
                }}
                className={btnSecondaryClass}
              >
                {t.skipForNow}
              </button>
              <button
                type="submit"
                disabled={loading}
                className={isEmbedded ? `${styles.embeddedBtnPrimary} flex-1 !bg-green-600 hover:!brightness-110` : 'flex-1 bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50'}
              >
                {loading ? t.btnProcessing : t.submitCreateAccount}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </>
  );

  if (isEmbedded) {
    return <div className={styles.embeddedRoot}>{stepContent}</div>;
  }

  return (
    <>
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t.pageTitle}</h1>
        <p className="text-gray-600">
          {t.pageSubtitlePrefix} <strong>{event.title}</strong>
        </p>
      </div>
      {stepContent}
    </>
  );
}
